/**
 * useSSEGenerate — 범용 SSE 스트리밍 생성 훅
 *
 * POST 요청으로 SSE 스트림을 열고 이벤트를 상태로 관리한다.
 * streamFetch (web/src/lib/sse.ts)를 활용하며,
 * 폴링 폴백은 포함하지 않는다 (필요 시 호출부에서 처리).
 *
 * @example
 *   const sse = useSSEGenerate<MyEvent>()
 *   sse.start({ url: '/api/generate', buildBody: () => JSON.stringify(body) })
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { streamFetch } from '@/lib/sse'

export type Phase = 'idle' | 'generating' | 'done' | 'error'

interface UseSSEGenerateOptions {
  /** SSE 엔드포인트 경로 (API_BASE 자동 추가됨) */
  url: string
  /** 요청 body 빌더 — JSON.stringify 또는 FormData 반환 */
  buildBody: () => BodyInit
}

export function useSSEGenerate<TEvent extends { type: string } = { type: string }>() {
  const [phase, setPhase] = useState<Phase>('idle')
  const [events, setEvents] = useState<TEvent[]>([])
  const [error, setError] = useState<string | null>(null)

  const abortRef = useRef<AbortController | null>(null)

  const pushEvent = useCallback((event: TEvent) => {
    setEvents((prev) => [...prev, event])
  }, [])

  const abort = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
  }, [])

  const start = useCallback(
    (options: UseSSEGenerateOptions) => {
      abort()

      setPhase('generating')
      setEvents([])
      setError(null)

      const controller = new AbortController()
      abortRef.current = controller

      const body = options.buildBody()

      streamFetch<TEvent>(
        options.url,
        { method: 'POST', body, signal: controller.signal },
        {
          onEvent: (raw) => {
            pushEvent(raw)

            if (raw.type === 'error') {
              const errMsg = (raw as Record<string, unknown>).error
              setError(typeof errMsg === 'string' ? errMsg : 'Unknown error')
              setPhase('error')
              return
            }

            if (raw.type === 'done') {
              setPhase('done')
              return
            }
          },
          onError: (err) => {
            const message = err.message || 'SSE 연결 오류'
            pushEvent({ type: 'error', error: message } as unknown as TEvent)
            setError(message)
            setPhase('error')
          },
          onDone: () => {
            // streamFetch onDone은 스트림 종료 시 호출
            // 'done' 이벤트가 먼저 도착하면 phase가 이미 설정됨
            setPhase((prev) => (prev === 'generating' ? 'done' : prev))
          },
        },
      )
    },
    [abort, pushEvent],
  )

  const reset = useCallback(() => {
    abort()
    setPhase('idle')
    setEvents([])
    setError(null)
  }, [abort])

  return { start, abort, reset, phase, events, error } as const
}
